// util/date.ts

export function getTodayString(): string {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, "0")
  const day = String(today.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function getTodayStringDot(): string {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, "0")
  const day = String(today.getDate()).padStart(2, "0")
  return `${year}.${month}.${day}`
}

type OutputFormat = "YYYY-MM-DD" | "YYYY년 MM월 DD일" | "YYYYMMDD"

export function formatDateString(input: string, outputFormat: OutputFormat): string {
  if (!input) return ""
  // 입력값에서 숫자만 추출
  const numeric = input.replace(/\D/g, "") // "20250711" 형태

  if (numeric.length !== 8) return "" // 잘못된 날짜 길이

  const year = numeric.slice(0, 4)
  const month = numeric.slice(4, 6)
  const day = numeric.slice(6, 8)

  const date = new Date(Number(year), Number(month) - 1, Number(day))

  if (isNaN(date.getTime())) return "" // 유효하지 않은 날짜 처리

  const pad = (n: number) => String(n).padStart(2, "0")

  switch (outputFormat) {
    case "YYYY-MM-DD":
      return `${year}-${pad(Number(month))}-${pad(Number(day))}`
    case "YYYY년 MM월 DD일":
      return `${year}년 ${pad(Number(month))}월 ${pad(Number(day))}일`
    case "YYYYMMDD":
      return `${year}${pad(Number(month))}${pad(Number(day))}`
    default:
      return input
  }
}

export function getAge(birthDateString: string): number {
  let birthDate: Date

  // 1. 숫자 8자리 (YYYYMMDD)
  if (/^\d{8}$/.test(birthDateString)) {
    const year = parseInt(birthDateString.slice(0, 4))
    const month = parseInt(birthDateString.slice(4, 6)) - 1
    const day = parseInt(birthDateString.slice(6, 8))
    birthDate = new Date(year, month, day)
  }
  // 2. ISO 형식 (YYYY-MM-DD 또는 YYYY/MM/DD 등)
  else if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(birthDateString)) {
    // 날짜 구분자 통일
    const normalized = birthDateString.replace(/-/g, "/")
    birthDate = new Date(normalized)
  }
  // 3. 유효하지 않은 형식
  else {
    throw new Error("지원되지 않는 생년월일 형식입니다.")
  }

  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()

  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

/**
 * 날짜/시간을 조건에 따라 포맷팅
 * - 오늘이면: "16:30" (시간만)
 * - 다른 날이면: "2025-10-13 16:30" (날짜 + 시간)
 * @param dateTimeString ISO 형식 날짜 문자열 (예: "2025-10-13T16:30:00")
 * @returns 포맷된 문자열
 */
export function formatReservationDateTime(dateTimeString: string | null | undefined): string {
  if (!dateTimeString) return "-"

  const date = new Date(dateTimeString)
  if (isNaN(date.getTime())) return "-"

  const today = new Date()
  const isToday = date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate()

  const hours = String(date.getHours()).padStart(2, "0")
  const minutes = String(date.getMinutes()).padStart(2, "0")
  const timeStr = `${hours}:${minutes}`

  if (isToday) {
    return timeStr
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day} ${timeStr}`
}
